// Feature spec 17, phase 3: what the entry point list, form, Overview band and deployment side share.
import type {
  EdgeKind,
  EntryPointRouteStatus,
  EntryPointSettings,
  NetworkKinds,
  OriginAccessMode,
  OriginDetachBlockedDetails,
} from '../../api/types';
import { strings } from '../../strings';

const e = strings.networks.entryPoints;

/**
 * Feature spec 17, phase 5 (user decision 2026-11-03): kinds the UI keeps greyed out even when the registry says
 * `available`, with their reason. AWS Global Accelerator is deferred: its TLS pass-through can't present a certificate
 * for a custom domain.
 */
export const HELD_EDGE_KINDS: Record<string, string> = {
  'aws-global-accelerator': e.aws.heldReason,
};

/** The registry's edges with the held kinds turned `coming-later` with their reason (whatever the registry says). */
export function withHeldKinds(edges: EdgeKind[]): EdgeKind[] {
  return edges.map((k) =>
    HELD_EDGE_KINDS[k.kind]
      ? {
          ...k,
          status: 'coming-later',
          // The registry's own deferred reason wins (spec 18 5.10 `deferred`).
          reason:
            k.status === 'coming-later' && k.reason?.text && k.reason.text !== 'Coming later.'
              ? k.reason
              : { text: HELD_EDGE_KINDS[k.kind] },
        }
      : k,
  );
}

/**
 * Feature spec 17, phase 7 (the user's instruction of 2026-11-04): add-ons the UI keeps greyed out (`aria-disabled`, with
 * their reason) whatever the registry says. How Cloud Armor Enterprise is offered (Annual commitment or Paygo) is still
 * being decided; until then its switch can't be turned on.
 */
export const HELD_ADD_ONS: Record<string, string> = {
  'cloud-armor-enterprise-annual': e.gcp.enterpriseHeld,
};

/** Why an add-on is held back: the registry's reason while it is `coming-later`, else the UI's own hold, else null. */
export function heldReason(addOn: {
  id: string;
  status?: string;
  reason?: { text: string } | null;
}): string | null {
  if (addOn.status === 'coming-later')
    return addOn.reason?.text ?? HELD_ADD_ONS[addOn.id] ?? e.gcp.enterpriseHeld;
  return HELD_ADD_ONS[addOn.id] ?? null;
}

/** The WAF add-ons: Azure's `waf`, from phase 5 `aws-waf`, from phase 7 `cloud-armor` (spec 18 5.1 `IsWaf`). */
export function isWaf(id: string): boolean {
  return id === 'waf' || id === 'aws-waf' || id === 'cloud-armor';
}

/** The registry's commitment of an add-on (Shield Advanced), or null. */
export function commitmentOf(kinds: NetworkKinds | undefined, kind: EdgeKind | undefined, addOnId: string) {
  const id = kind?.addOns.find((a) => a.id === addOnId)?.commitment;
  return (id && kinds?.commitments.find((c) => c.addOn === id)) || null;
}

/** The text the API expects: "<network name> <years>-year" (spec 04 6.14.2 Commitments). */
export function expectedConfirmation(network: string, terms: { months: number }): string {
  return `${network.trim()} ${Math.round(terms.months / 12)}-year`;
}

/** "Nov 3, 2027". */
export function dayWords(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** "Cloudflare Pro: USD 25/month per zone (your Cloudflare plan)" from the item "Cloudflare Pro (edge)" (phase 8). */
export function cloudflareCostLine(item: string, currency: string, monthly: number): string {
  const plan = item.replace(/^Cloudflare /, '').replace(/ \([^)]*\)$/, '');
  return monthly === 0 ? e.cloudflare.costFree(plan) : e.cloudflare.costLine(plan, currency, monthly);
}

/** Strongest first (spec 11 4.10). */
export const ACCESS_ORDER: OriginAccessMode[] = [
  'private-link',
  'private-endpoint',
  'vpc-origin',
  'serverless-neg',
  'access-restriction',
  'allow-list-and-header',
  'header',
  'none',
];

export const ACCESS_STRENGTH: Record<OriginAccessMode, 'strongest' | 'strong' | 'medium' | 'weak' | 'none'> =
  {
    'private-link': 'strongest',
    'private-endpoint': 'strongest',
    'vpc-origin': 'strongest',
    'serverless-neg': 'strong',
    'access-restriction': 'strong',
    'allow-list-and-header': 'medium',
    header: 'weak',
    none: 'none',
  };

export function edgeOf(kinds: NetworkKinds | undefined, kind: string): EdgeKind | undefined {
  return kinds?.edges.find((k) => k.kind === kind);
}

/** Whether an add-on is on: the saved `enabled`, else the registry's `default`. */
export function addOnEnabled(ep: Pick<EntryPointSettings, 'addOns'>, kind: EdgeKind | undefined, id: string) {
  const saved = ep.addOns?.[id];
  if (saved) return saved.enabled;
  return kind?.addOns.find((a) => a.id === id)?.default ?? false;
}

/** The WAF's mode when it is on, else null. */
export function wafMode(
  ep: EntryPointSettings,
  kind: EdgeKind | undefined,
): 'prevention' | 'detection' | null {
  const id = kind?.addOns.find((a) => isWaf(a.id))?.id;
  if (!id || !addOnEnabled(ep, kind, id)) return null;
  return ep.addOns?.[id]?.mode ?? 'prevention';
}

/** "Azure Front Door · Premium", "Azure Application Gateway · WAF_v2 · northeurope hub". */
export function kindLine(ep: EntryPointSettings, kind: EdgeKind | undefined): string {
  const label = kind?.label ?? ep.kind;
  const tier = kind?.tiers.find((t) => t.id === ep.tier)?.label ?? ep.tier ?? null;
  const parts = [label, tier, ep.region ? e.regionHub(ep.region) : null].filter(Boolean);
  return parts.join(' · ');
}

export function tierLabel(ep: EntryPointSettings, kind: EdgeKind | undefined): string | null {
  return kind?.tiers.find((t) => t.id === ep.tier)?.label ?? ep.tier ?? null;
}

export interface AddOnChip {
  id: string;
  text: string;
  tone: 'success' | 'warning' | 'neutral';
  /** Read with the chip (detection mode's consequence). */
  hint?: string;
}

/** The card header's add-on chips (spec 11 6.15.10): WAF with its mode, CDN, private origins, DDoS. */
export function addOnChips(ep: EntryPointSettings, kind: EdgeKind | undefined): AddOnChip[] {
  const chips: AddOnChip[] = [];
  const c = e.chips;
  for (const addOn of kind?.addOns ?? []) {
    const on = addOnEnabled(ep, kind, addOn.id);
    // Commitment add-ons have their own chip with the subscription's state (EntryPointCard).
    if (addOn.commitment) continue;
    if (isWaf(addOn.id)) {
      if (!on) continue;
      const settings = ep.addOns?.[addOn.id];
      const mode = settings?.mode ?? 'prevention';
      // AWS WAF and Cloud Armor have no tiers: their managed rules default to on; Cloudflare's run on every plan.
      const managed =
        addOn.id === 'aws-waf' || addOn.id === 'cloud-armor' || ep.kind === 'cloudflare'
          ? (settings?.managedRules ?? true)
          : settings?.managedRules && ep.tier === 'premium';
      chips.push(
        mode === 'detection'
          ? { id: addOn.id, text: c.wafDetection, tone: 'warning', hint: c.wafDetectionHint }
          : {
              id: addOn.id,
              text: managed ? `${c.wafPrevention} · ${c.managedRules}` : c.wafPrevention,
              tone: 'success',
            },
      );
    } else if (addOn.id === 'cdn')
      chips.push({
        id: 'cdn',
        text:
          ep.kind === 'cloudflare'
            ? on
              ? e.cloudflare.cachingOn
              : e.cloudflare.cachingOff
            : on
              ? c.cdnOn
              : c.cdnOff,
        tone: 'neutral',
      });
    else if (ep.kind === 'cloudflare' && addOn.id === 'ddos')
      // Feature spec 17, phase 8: Cloudflare's DDoS protection is always on.
      chips.push({ id: 'ddos', text: `${e.cloudflare.ddos} · ${e.cloudflare.alwaysOn}`, tone: 'neutral' });
    else if (on)
      chips.push({
        id: addOn.id,
        text:
          addOn.id === 'ddos-ip-protection'
            ? c.ddosIp
            : addOn.id === 'ddos-network-protection'
              ? c.ddosNetwork
              : addOn.id === 'private-origins'
                ? c.privateOrigins
                : addOn.label,
        tone: 'neutral',
      });
  }
  return chips;
}

/** The modes a route may choose for a platform on this entry point (Private Link needs Premium's private origins). */
export function offeredModes(
  ep: Pick<EntryPointSettings, 'tier' | 'addOns'>,
  kind: EdgeKind | undefined,
  platform: string,
): OriginAccessMode[] {
  const origin = kind?.origins.find((o) => o.platform === platform);
  if (!origin) return [];
  return (origin.access as OriginAccessMode[]).filter(
    (m) =>
      (m !== 'private-link' || (ep.tier === 'premium' && addOnEnabled(ep, kind, 'private-origins'))) &&
      // Feature spec 17, phase 5: CloudFront's VPC origins need its `vpc-origins` add-on (on by default).
      (m !== 'vpc-origin' ||
        !kind?.addOns.some((a) => a.id === 'vpc-origins') ||
        addOnEnabled(ep, kind, 'vpc-origins')),
  );
}

/** What `auto` decides: the strongest offered mode, or null where the registry's default is null (D12). */
export function preselectedMode(
  ep: Pick<EntryPointSettings, 'tier' | 'addOns'>,
  kind: EdgeKind | undefined,
  platform: string,
): OriginAccessMode | null {
  const origin = kind?.origins.find((o) => o.platform === platform);
  if (!origin || origin.default === null) return null;
  const modes = offeredModes(ep, kind, platform);
  return ACCESS_ORDER.find((m) => modes.includes(m)) ?? null;
}

/** The host and path rows of a route ("shop.example.com/api/*"). */
export function hostPaths(
  route: Pick<EntryPointRouteStatus, 'hostnames' | 'paths'>,
  edgeHost: string | null,
) {
  const hosts = route.hostnames.length ? route.hostnames : [edgeHost ?? e.edgeHost];
  const paths = route.paths.length ? route.paths : ['/*'];
  return hosts.flatMap((h) => paths.map((p) => `${h}${p}`));
}

/** The vault's name from a Key Vault secret ID (`https://kv-certs.vault.azure.net/secrets/…` → `kv-certs`). */
export function vaultOf(secretId: string | undefined): string {
  if (!secretId) return 'the vault';
  try {
    return new URL(secretId).hostname.split('.')[0] || secretId;
  } catch {
    return secretId;
  }
}

/** An inactive route's reason in words (6.15.29). */
export function inactiveWords(code: string | null, deployment: string, network: string): string {
  switch (code) {
    case 'ROUTE_ORIGIN_NOT_ATTACHED':
      return e.codes.ROUTE_ORIGIN_NOT_ATTACHED(deployment, network);
    case 'ROUTE_ORIGIN_UNKNOWN':
      return e.codes.ROUTE_ORIGIN_UNKNOWN;
    default:
      return code ?? '';
  }
}

// Feature spec 17, phase 7 (spec 11 6.15 item 38): `ORIGIN_DETACH_BLOCKED`.
/** The `details` of a Problem when it is an `ORIGIN_DETACH_BLOCKED` with routes, else null. */
export function detachBlockedDetails(problem: unknown): OriginDetachBlockedDetails | null {
  const details = (problem as { details?: Partial<OriginDetachBlockedDetails> } | null)?.details;
  return details && typeof details.networkId === 'string' && Array.isArray(details.routes)
    ? (details as OriginDetachBlockedDetails)
    : null;
}

/** One sentence per blocking route: "web serves route shop of entry point public … apply Production first." */
export function detachBlockedSentences(details: OriginDetachBlockedDetails): string[] {
  return details.routes.map((r) =>
    e.codes.ORIGIN_DETACH_BLOCKED(r.resourceId, r.routeId, r.entryPointId, details.networkName),
  );
}
